import React, { useState, useCallback, memo } from 'react';
import { Box, Typography, Grid, Card, CardActionArea, CardContent, IconButton, Button, Alert } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';

const BUTTON_ICONS = {
  jain: '🪷',
  veg: '🥬',
  veg_nonveg: '🍱',
  veg_starters: '🥟',
  nonveg_starters: '🍢',
  veg_main_course: '🍛'
};

// Hoisted: static meal types do not depend on props/state
const MEAL_TYPES = [
  { 
    id: 'jain', 
    name: 'Jain',
    icon: '🥗' // Bowl icon for Jain food
  },
  { 
    id: 'veg', 
    name: 'Veg',
    icon: '🥬' // Leafy greens for veg
  },
  { 
    id: 'veg_nonveg', 
    name: 'Veg + NonVeg',
    icon: '🍗' // Chicken leg for non-veg
  },
].map((meal) => ({ ...meal, icon: BUTTON_ICONS[meal.id] }));

// Client-approved menu choices shown with the meal type selection.
const MENU_PACKAGES = [
  {
    id: 'veg_starters',
    title: 'Only Starter Veg',
    icon: '🥗',
    details: 'Choose any 5 veg starters',
    price: 400
  },
  {
    id: 'nonveg_starters',
    title: 'Only Starter Non Veg',
    icon: '🍗',
    details: 'Choose 6 starters (3 veg + 3 non-veg)',
    price: 550
  },
  {
    id: 'veg_main_course',
    title: 'Only Main Course Veg',
    icon: '🍛',
    details: '3 mains, 1 rice, 2 breads & 1 dessert. Raita and salad included.',
    price: 350
  }
].map((menuPackage) => ({ ...menuPackage, icon: BUTTON_ICONS[menuPackage.id] }));

const MIN_DEFAULT_PAX = 15;
const MIN_JAIN_PAX = 15;
const MIN_CUSTOMIZED_TOTAL_PAX = 15;
const MIN_CUSTOMIZED_GROUP_PAX = 0;

const isVegOnlySelection = (selection) => (
  selection === 'veg' || selection === 'veg_starters' || selection === 'veg_main_course'
);

const isMixedSelection = (selection) => (
  selection === 'veg_nonveg' || selection === 'nonveg_starters'
);

const isPackageSelection = (selection) => (
  selection === 'veg_starters' || selection === 'nonveg_starters' || selection === 'veg_main_course'
);

const isSinglePaxPackageSelection = (selection) => (
  selection === 'veg_starters' || selection === 'veg_main_course'
);

const getAdjustedCustomizedCounts = (changedType, changedValue, currentVeg, currentNonVeg) => {
  const safeChangedValue = Math.max(MIN_CUSTOMIZED_GROUP_PAX, Number(changedValue) || 0);
  const currentPairedValue = changedType === 'veg' ? currentNonVeg : currentVeg;
  const pairedValue = safeChangedValue + currentPairedValue < MIN_CUSTOMIZED_TOTAL_PAX
    ? MIN_CUSTOMIZED_TOTAL_PAX - safeChangedValue
    : currentPairedValue;

  if (changedType === 'veg') {
    return { veg: safeChangedValue, nonVeg: pairedValue };
  }

  return { veg: pairedValue, nonVeg: safeChangedValue };
};

const MealTypeStep = ({ onNext, updateBookingData, initialGuestCount }) => {
  const [selectedMealType, setSelectedMealType] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [guestCountError, setGuestCountError] = useState('');
  // Separate count states for each meal type
  const initialCount = Math.max(MIN_DEFAULT_PAX, initialGuestCount || MIN_DEFAULT_PAX);
  const [pureVegCount, setPureVegCount] = useState(initialCount);
  const [comboVegCount, setComboVegCount] = useState(10);
  const [comboNonVegCount, setComboNonVegCount] = useState(5);
  const [packageGuestCount, setPackageGuestCount] = useState(MIN_DEFAULT_PAX);
  const [jainCount, setJainCount] = useState(Math.max(MIN_JAIN_PAX, initialGuestCount || MIN_JAIN_PAX));
  const activeSelection = selectedMealType || selectedPackage;

  const handleMealTypeSelect = useCallback((meal) => {
    setSelectedMealType(meal.id);
    setSelectedPackage(null);
    setGuestCountError('');
    
    // Only update relevant counts based on selected meal type
    const mealData = {
      mealType: meal.name,
      menuPackage: null,
      menuPackageName: null,
      pricePerPerson: null,
      menuPackageDetails: null
    };
    
    if (meal.id === 'veg') {
      mealData.vegCount = pureVegCount;
      mealData.nonVegCount = 0; // Reset non-veg count for pure veg
      mealData.jainCount = 0;
    } else if (meal.id === 'veg_nonveg') {
      mealData.vegCount = comboVegCount;
      mealData.nonVegCount = comboNonVegCount;
      mealData.jainCount = 0;
    } else if (meal.id === 'jain') {
      mealData.vegCount = 0;
      mealData.nonVegCount = 0;
      mealData.jainCount = Math.max(MIN_JAIN_PAX, jainCount);
    }
    
    updateBookingData(mealData);
  }, [pureVegCount, comboVegCount, comboNonVegCount, jainCount, updateBookingData]);

  const handlePackageSelect = useCallback((menuPackage) => {
    const isMixedPackage = menuPackage.id === 'nonveg_starters';
    const isVegPackage = !isMixedPackage;

    setSelectedMealType(null);
    setSelectedPackage(menuPackage.id);
    setGuestCountError('');
    if (isVegPackage) setPureVegCount(MIN_DEFAULT_PAX);
    setPackageGuestCount(MIN_DEFAULT_PAX);
    if (isMixedPackage) {
      setComboVegCount(10);
      setComboNonVegCount(5);
    }
    updateBookingData({
      mealType: isMixedPackage ? 'Veg + NonVeg' : 'Veg',
      vegCount: isMixedPackage ? 10 : MIN_DEFAULT_PAX,
      nonVegCount: isMixedPackage ? 5 : 0,
      jainCount: 0,
      menuPackage: menuPackage.id,
      menuPackageName: menuPackage.title,
      pricePerPerson: menuPackage.price,
      menuPackageDetails: menuPackage.details
    });
  }, [updateBookingData]);

  const handlePackageGuestCountChange = useCallback((amount) => {
    const nextCount = Math.max(MIN_DEFAULT_PAX, packageGuestCount + amount);
    const isNonVegStarterPackage = activeSelection === 'nonveg_starters';
    const nextVegCount = isNonVegStarterPackage ? Math.ceil((nextCount * 2) / 3) : nextCount;
    const nextNonVegCount = isNonVegStarterPackage ? nextCount - nextVegCount : 0;

    setPackageGuestCount(nextCount);
    if (isNonVegStarterPackage) {
      setComboVegCount(nextVegCount);
      setComboNonVegCount(nextNonVegCount);
    } else {
      setPureVegCount(nextCount);
    }
    updateBookingData({ vegCount: nextVegCount, nonVegCount: nextNonVegCount });
  }, [activeSelection, packageGuestCount, updateBookingData]);

  const handleVegCountChange = useCallback((amount) => {
    if (isVegOnlySelection(activeSelection)) {
      const newCount = Math.max(MIN_DEFAULT_PAX, pureVegCount + amount);
      setPureVegCount(newCount);
      updateBookingData({ vegCount: newCount });
    } else if (isMixedSelection(activeSelection)) {
      const nextCounts = getAdjustedCustomizedCounts('veg', comboVegCount + amount, comboVegCount, comboNonVegCount);
      setGuestCountError('');
      setComboVegCount(nextCounts.veg);
      setComboNonVegCount(nextCounts.nonVeg);
      updateBookingData({ vegCount: nextCounts.veg, nonVegCount: nextCounts.nonVeg });
    }
  }, [activeSelection, pureVegCount, comboVegCount, comboNonVegCount, updateBookingData]);

  const handleNonVegCountChange = useCallback((change) => {
    const nextCounts = getAdjustedCustomizedCounts('nonVeg', comboNonVegCount + change, comboVegCount, comboNonVegCount);
    setGuestCountError('');
    setComboVegCount(nextCounts.veg);
    setComboNonVegCount(nextCounts.nonVeg);
    
    if (isMixedSelection(activeSelection)) {
      updateBookingData({ vegCount: nextCounts.veg, nonVegCount: nextCounts.nonVeg });
    }
  }, [activeSelection, comboVegCount, comboNonVegCount, updateBookingData]);

  const handleJainCountChange = useCallback((amount) => {
    const newCount = Math.max(MIN_JAIN_PAX, jainCount + amount);
    setGuestCountError('');
    setJainCount(newCount);
    
    // Only update booking data if jain is selected
    if (activeSelection === 'jain') {
      updateBookingData({ jainCount: newCount });
    }
  }, [activeSelection, jainCount, updateBookingData]);

  const handleProceed = useCallback(() => {
    if (activeSelection === 'jain' && jainCount < MIN_JAIN_PAX) {
      setGuestCountError(`Jain pax must be minimum ${MIN_JAIN_PAX}.`);
      return;
    }

    if (isMixedSelection(activeSelection) && comboVegCount + comboNonVegCount < MIN_CUSTOMIZED_TOTAL_PAX) {
      setGuestCountError(`Customized total pax must be minimum ${MIN_CUSTOMIZED_TOTAL_PAX}.`);
      return;
    }

    // Get the proper meal type name from the selected meal type ID
    const selectedMeal = MEAL_TYPES.find(meal => meal.id === activeSelection);
    const mealTypeName = selectedMeal
      ? selectedMeal.name
      : isMixedSelection(activeSelection) ? 'Veg + NonVeg' : 'Veg';
    
    // Save the meal type and guest count data
    const mealData = {
      mealType: mealTypeName,
      vegCount: activeSelection === 'nonveg_starters'
        ? comboVegCount
        : isSinglePaxPackageSelection(activeSelection)
          ? packageGuestCount
          : isVegOnlySelection(activeSelection) ? pureVegCount : isMixedSelection(activeSelection) ? comboVegCount : 0,
      nonVegCount: activeSelection === 'nonveg_starters'
        ? comboNonVegCount
        : isSinglePaxPackageSelection(activeSelection)
          ? 0
          : isMixedSelection(activeSelection) ? comboNonVegCount : 0,
      jainCount: activeSelection === 'jain' ? jainCount : 0
    };
    
    
    updateBookingData(mealData);
    
    // Call onNext to let BookingWizard handle the completion
    setTimeout(() => {
      onNext();
    }, 300);
  }, [activeSelection, pureVegCount, comboVegCount, comboNonVegCount, packageGuestCount, jainCount, updateBookingData, onNext]);

  return (
    <Box sx={{ 
      textAlign: 'center', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column'
    }}>
      <Typography 
        variant="h4" 
        sx={{ 
          fontWeight: 'bold', 
          mt: { xs: -0.5, sm: -0.75 },
          mb: { xs: 0.25, sm: 0.5 },
          fontSize: { xs: '1.3rem', sm: '2rem' }
        }}
      >
        Meal Type
      </Typography>
      
      {/* Meal Type Selection */}
      <Grid 
        container 
        spacing={{ xs: 0.5, sm: 0.5, md: 0.75 }}
        sx={{ 
          mt: { xs: -0.25, sm: -0.25 },
          mb: { xs: 1, sm: 1.75 },
          flex: '0 0 auto',
          justifyContent: { xs: 'flex-start', sm: 'center', md: 'center' },
          maxWidth: { sm: '600px', md: '700px' },
          margin: { sm: '0 auto', md: '0 auto' }
        }}
      >
        {MEAL_TYPES.map((meal) => (
          <Grid item xs={4} sm={3} md={3} key={meal.id}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: { xs: 2, sm: 3 },
                borderColor: selectedMealType === meal.id ? '#ff6b35' : 'grey.300',
                borderWidth: 2,
                mx: 'auto',
                transition: 'all 0.2s ease-in-out',
                height: { xs: '64px', sm: '72px' },
                maxWidth: { xs: 110, sm: 170 },
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: 2
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleMealTypeSelect(meal)} 
                sx={{ 
                  p: { xs: 0.75, sm: 1 },
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CardContent sx={{ p: 0, textAlign: 'center' }}>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      mb: { xs: 0.35, sm: 0.5 },
                      fontSize: { xs: '1.1rem', sm: '1.4rem' }
                    }}
                  >
                    {meal.icon}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'medium',
                      fontSize: { xs: '0.64rem', sm: '0.85rem' },
                      lineHeight: 1.1,
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      wordBreak: 'break-word'
                    }}
                  >
                    {meal.name}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Client menu package choices */}
      <Grid
        container
        spacing={0}
        sx={{
          mt: { xs: 0.75, sm: 1.25 },
          mb: { xs: 1, sm: 1.5 },
          px: { xs: 0.25, sm: 0 },
          justifyContent: 'center',
          flex: '0 0 auto'
        }}
      >
        {MENU_PACKAGES.map((menuPackage) => (
          <Grid item xs={4} sm={4} key={menuPackage.id} sx={{ display: 'flex' }}>
            <Card
              variant="outlined"
              sx={{
                width: '100%',
                maxWidth: { xs: 110, sm: 200 },
                mx: 'auto',
                borderRadius: 2,
                borderColor: selectedPackage === menuPackage.id ? '#ff6b35' : 'grey.300',
                borderWidth: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': { boxShadow: 2 }
              }}
            >
              <CardActionArea
                onClick={() => handlePackageSelect(menuPackage)}
                sx={{
                  height: '100%',
                  minHeight: { xs: 58, sm: 68 },
                  p: { xs: 0.6, sm: 0.75 },
                  textAlign: 'center'
                }}
              >
                <CardContent sx={{ p: '0 !important', textAlign: 'center' }}>
                  <Typography sx={{ mb: { xs: 0.2, sm: 0.35 }, fontSize: { xs: '1rem', sm: '1.25rem' }, lineHeight: 1 }}>
                    {menuPackage.icon}
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.58rem', sm: '0.78rem' }, lineHeight: 1.05 }}>
                    {menuPackage.title}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Guest Count */}
      <Typography 
        variant="h4" 
        sx={{ 
          fontWeight: 'bold', 
          mb: { xs: 0.75, sm: 1.25 },
          fontSize: { xs: '1.2rem', sm: '2rem' }
        }}
      >
        Guest Count
      </Typography>
    

      {/* Guest Count Controls - Show for all meal types */}
      {(isVegOnlySelection(activeSelection) || isMixedSelection(activeSelection) || activeSelection === 'jain') && (
        <Box sx={{ 
          mb: { xs: 1, sm: 1.75 },
          px: { xs: 2, sm: 4 },
          flex: '0 0 auto'
        }}>
          {/* For Veg + NonVeg: Show both controls side by side */}
          {isSinglePaxPackageSelection(activeSelection) ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 'medium', fontSize: { xs: '0.95rem', sm: '1rem' }, whiteSpace: 'nowrap' }}
              >
                No of Pax:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: 2, bgcolor: 'white', overflow: 'hidden' }}>
                <IconButton
                  onClick={() => handlePackageGuestCountChange(-1)}
                  size="small"
                  sx={{ borderRadius: 0, width: 32, height: 32, '&:hover': { bgcolor: 'grey.100' } }}
                >
                  <Remove sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center', fontSize: '1rem', fontWeight: 'medium', px: 1, py: 0.5 }}>
                  {packageGuestCount}
                </Typography>
                <IconButton
                  onClick={() => handlePackageGuestCountChange(1)}
                  size="small"
                  sx={{ borderRadius: 0, width: 32, height: 32, '&:hover': { bgcolor: 'grey.100' } }}
                >
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          ) : isMixedSelection(activeSelection) ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'row', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 2, sm: 4, md: 6 },
              flexWrap: { xs: 'wrap', sm: 'nowrap' }
            }}>
              {/* Veg Count */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 2
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 'medium',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    whiteSpace: 'nowrap'
                  }}
                >
                  No of Pax (Veg):
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  bgcolor: 'white',
                  overflow: 'hidden'
                }}>
                  <IconButton 
                    onClick={() => handleVegCountChange(-1)} 
                    size="small"
                    sx={{ 
                      borderRadius: 0,
                      width: 32,
                      height: 32,
                      '&:hover': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  >
                    <Remove sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      minWidth: 40, 
                      textAlign: 'center',
                      fontSize: '1rem',
                      fontWeight: 'medium',
                      px: 1,
                      py: 0.5
                    }}
                  >
                    {isVegOnlySelection(activeSelection) ? pureVegCount : comboVegCount}
                  </Typography>
                  <IconButton 
                    onClick={() => handleVegCountChange(1)} 
                    size="small"
                    sx={{ 
                      borderRadius: 0,
                      width: 32,
                      height: 32,
                      '&:hover': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  >
                    <Add sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Non-Veg Count */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 2
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 'medium',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    whiteSpace: 'nowrap'
                  }}
                >
                  No of Pax (NonVeg):
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  bgcolor: 'white',
                  overflow: 'hidden'
                }}>
                  <IconButton 
                    onClick={() => handleNonVegCountChange(-1)} 
                    size="small"
                    sx={{ 
                      borderRadius: 0,
                      width: 32,
                      height: 32,
                      '&:hover': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  >
                    <Remove sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      minWidth: 40, 
                      textAlign: 'center',
                      fontSize: '1rem',
                      fontWeight: 'medium',
                      px: 1,
                      py: 0.5
                    }}
                  >
                    {comboNonVegCount}
                  </Typography>
                  <IconButton 
                    onClick={() => handleNonVegCountChange(1)} 
                    size="small"
                    sx={{ 
                      borderRadius: 0,
                      width: 32,
                      height: 32,
                      '&:hover': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  >
                    <Add sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ) : isVegOnlySelection(activeSelection) ? (
            /* For Veg only: Show single control */
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 4
            }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 'medium',
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  whiteSpace: 'nowrap'
                }}
              >
                No of Pax (Veg):
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                border: '1px solid #ddd',
                borderRadius: 2,
                bgcolor: 'white',
                overflow: 'hidden'
              }}>
                <IconButton 
                  onClick={() => handleVegCountChange(-1)} 
                  size="small"
                  sx={{ 
                    borderRadius: 0,
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: 'grey.100'
                    }
                  }}
                >
                  <Remove sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    minWidth: 40, 
                    textAlign: 'center',
                    fontSize: '1rem',
                    fontWeight: 'medium',
                    px: 1,
                    py: 0.5
                  }}
                >
                  {pureVegCount}
                </Typography>
                <IconButton 
                  onClick={() => handleVegCountChange(1)} 
                  size="small"
                  sx={{ 
                    borderRadius: 0,
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: 'grey.100'
                    }
                  }}
                >
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          ) : (
            /* For Jain: Show single control */
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 4
            }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 'medium',
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  whiteSpace: 'nowrap'
                }}
              >
                No of Pax:
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                border: '1px solid #ddd',
                borderRadius: 2,
                bgcolor: 'white',
                overflow: 'hidden'
              }}>
                <IconButton 
                  onClick={() => handleJainCountChange(-1)} 
                  size="small"
                  sx={{ 
                    borderRadius: 0,
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: 'grey.100'
                    }
                  }}
                >
                  <Remove sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    minWidth: 40, 
                    textAlign: 'center',
                    fontSize: '1rem',
                    fontWeight: 'medium',
                    px: 1,
                    py: 0.5
                  }}
                >
                  {jainCount}
                </Typography>
                <IconButton 
                  onClick={() => handleJainCountChange(1)} 
                  size="small"
                  sx={{ 
                    borderRadius: 0,
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: 'grey.100'
                    }
                  }}
                >
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {guestCountError && (
        <Alert severity="warning" sx={{ maxWidth: 420, mx: 'auto', mb: 1.5, textAlign: 'left' }}>
          {guestCountError}
        </Alert>
      )}

      {/* See Packages Button */}
      <Box sx={{ mt: { xs: 0, sm: 'auto' }, pt: { xs: 1, sm: 0 } }}>
        <Button
          variant="contained"
          onClick={handleProceed}
          disabled={!activeSelection}
          sx={{
            bgcolor: '#1e3a8a',
            color: 'white',
            py: { xs: 1.2, sm: 1.5 },
            px: { xs: 4, sm: 6 },
            fontSize: { xs: '1rem', sm: '1.1rem' },
            fontWeight: 600,
            borderRadius: 2,
            textTransform: 'none',
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '280px', sm: 'none' },
            mx: 'auto',
            '&:hover': {
              bgcolor: '#1e40af'
            },
            '&:disabled': {
              bgcolor: 'grey.300'
            }
          }}
        >
          Proceed
        </Button>
      </Box>
    </Box>
  );
};

export default memo(MealTypeStep);
